// src/moment/components/AsyncImage/AsyncImage.tsx
import React, { useEffect, useMemo, useRef, useState, forwardRef, ImgHTMLAttributes } from "react";
import "./AsyncImage.scss";

export interface AsyncImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  containerClassName?: string; // Renamed to avoid conflict if KenBurnsAsyncImage also has className
  staticSized?: boolean;
  onImageLoad?: () => void; // Callback when image is loaded (img.onload or complete)
}

export const AsyncImage = forwardRef<HTMLImageElement, AsyncImageProps>(
  (
    {
      src,
      alt = "",
      width = "100%",
      height = "100%",
      containerClassName,
      staticSized = false,
      onImageLoad,
      ...imgProps // Spread remaining img attributes like style, etc.
    }: AsyncImageProps,
    forwardedRef
  ) => {
    const [isLoaded, setIsLoaded] = useState(false);
    // Use an internal ref if no ref is forwarded, or combine if necessary,
    // but for this use case, the forwardedRef is primary for the img.
    const internalImgRef = useRef<HTMLImageElement>(null);
    const imgRef = forwardedRef || internalImgRef;

    useEffect(() => {
      // Check if image is already loaded (e.g., cached)
      const imageElement = (imgRef && 'current' in imgRef) ? imgRef.current : null;
      if (imageElement?.complete) {
        setIsLoaded(true);
        onImageLoad?.();
      } else {
        // Reset loaded state if src changes and image is not complete
        setIsLoaded(false);
      }
    }, [src, imgRef, onImageLoad]);

    const handleLoad = () => {
      setIsLoaded(true);
      onImageLoad?.();
    };

    const containerClass = useMemo(
      () => ["aimage__container", containerClassName].filter(Boolean).join(" "),
      [containerClassName]
    );
    const imageClass = useMemo(
      () => ["aimage", isLoaded ? "aimage--visible" : "aimage--loading"].join(" "),
      [isLoaded]
    );

    const imageEffectiveHeight = useMemo(() => {
      return staticSized ? height : '100%';
    }, [staticSized, height]);

    const containerStyleList = useMemo(() => {
      return {
        width: staticSized ? width : '100%',
        height: imageEffectiveHeight,
        minHeight: imageEffectiveHeight, // Ensure container respects image height
      };
    }, [staticSized, imageEffectiveHeight, width]);

    const shimmerStyle = useMemo(() => {
      // Shimmer should respect the explicit width/height if provided, otherwise 100%
      return {
        width: width,
        height: height,
      };
    }, [width, height]);


    return (
      <div className={containerClass} style={containerStyleList}>
        {!isLoaded && (
          <div className="aimage__shimmer" style={shimmerStyle} />
        )}
        <img
          {...imgProps} // Spread other native image attributes
          className={imageClass}
          ref={imgRef}
          src={src}
          alt={alt}
          width={width} // Set width/height attributes for layout, CSS will handle final display
          height={imageEffectiveHeight}
          onLoad={handleLoad}
          onError={() => {
            // Optional: Handle image loading errors
            setIsLoaded(false); // Or set an error state
          }}
        />
      </div>
    );
  }
);

AsyncImage.displayName = "AsyncImage"; // Good practice for forwardRef components

export default AsyncImage;
